import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  KeyboardEvent,
} from "react";
import { ActionIcon } from "@mantine/core";
import { NumericFormat } from "react-number-format";
import { InterviewQuestionInputType } from "../../types";
import { translateIdentity, type StaticTextTranslator } from "../../utils";
import { PhoneInput } from "./PhoneInput";
import styles from "./MessageInput.module.css";

const SendIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const TEXTAREA_LINE_HEIGHT_PX = 22;

interface MessageInputProps {
  onSend: (content: string) => void;
  disabled?: boolean;
  placeholder?: string;
  inputType?: InterviewQuestionInputType;
  /** Seed value for edit-mode prefill. The parent remounts this component
   * (key=editTarget.resetKey) when the edit target changes, so this is read
   * once into state — no prop-sync effect. */
  initialValue?: string;
  autoFocus?: boolean;
  translate?: StaticTextTranslator;
}

export function MessageInput({
  onSend,
  disabled = false,
  placeholder = "Enter message",
  inputType = InterviewQuestionInputType.TEXT,
  initialValue,
  autoFocus = false,
  translate = translateIdentity,
}: MessageInputProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Tracks the last value synchronously dispatched to setValue — see DEVOPS2-557.
  const lastHandledValueRef = useRef(initialValue ?? "");
  const [value, setValue] = useState(initialValue ?? "");
  const [isValid, setIsValid] = useState(true);
  const [showValidationError, setShowValidationError] = useState(false);
  const [inputElementRef, setInputElementRef] = useState<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null);
  const [phoneRegionSelectorRef, setPhoneRegionSelectorRef] =
    useState<HTMLInputElement | null>(null);

  // Edit-mode entry focus (a11y: focus moves to the prefilled input). Runs
  // once per mount — the parent key-remounts on each edit entry.
  const hasAutoFocusedRef = useRef(false);
  useEffect(() => {
    if (autoFocus && inputElementRef && !hasAutoFocusedRef.current) {
      hasAutoFocusedRef.current = true;
      inputElementRef.focus();
    }
  }, [autoFocus, inputElementRef]);

  const handleSend = useCallback(() => {
    const trimmed = value.trim();

    if (inputType === InterviewQuestionInputType.PHONE && trimmed && !isValid) {
      setShowValidationError(true);
      return;
    }

    if (trimmed) {
      onSend(trimmed);
      setValue("");
      setIsValid(true);
      setShowValidationError(false);
    }
  }, [inputType, onSend, isValid, value]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Auto-focus input when user starts typing alphanumeric characters.
  // Using a document-level listener rather than autofocus to prevent
  // blocking screen readers from reading the last message.
  useEffect(() => {
    const keyDownListener = ({
      altKey,
      ctrlKey,
      key,
      metaKey,
    }: globalThis.KeyboardEvent) => {
      if (
        inputElementRef &&
        document.activeElement !== inputElementRef &&
        document.activeElement !== phoneRegionSelectorRef &&
        // Assume the user is trying to do something other than type if a
        // non-shift modifier is pressed
        !(altKey || ctrlKey || metaKey) &&
        // Limit to alphanumeric inputs to avoid focusing on key presses
        // that likely aren't meant to be input
        key.match(/^[\p{L}|\p{N}]$/u)
      ) {
        inputElementRef.focus();
      }
    };

    document.addEventListener("keydown", keyDownListener);
    return () => document.removeEventListener("keydown", keyDownListener);
  }, [inputElementRef, phoneRegionSelectorRef]);

  // Keep lastHandledValueRef in sync when value changes externally (e.g. cleared
  // after send). Doesn't cause an extra render.
  useEffect(() => {
    lastHandledValueRef.current = value;
  }, [value]);

  // Resize wrapper as more lines of text are added to textarea
  useEffect(() => {
    if (
      inputType === InterviewQuestionInputType.LONG_TEXT &&
      wrapperRef.current &&
      inputElementRef
    ) {
      const wrapper = wrapperRef.current;
      // Collapse to 1px, read scrollHeight, then restore — this lets
      // the wrapper grow up to max-height (~4 lines) before scrolling
      inputElementRef.style.height = "1px";
      wrapper.style.height = `${inputElementRef.scrollHeight + 2}px`;
      inputElementRef.style.height = "100%";
    }
  }, [value, inputElementRef, inputType]);

  const inputEl = useMemo(() => {
    const commonProps = {
      value,
      onChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        // Dedupe via ref rather than state — same stale-closure fix as DEVOPS2-557.
        const next = e.currentTarget.value;
        if (next === lastHandledValueRef.current) {
          return;
        }
        lastHandledValueRef.current = next;
        setValue(next);
      },
      onKeyDown: handleKeyDown,
      placeholder,
      disabled,
      className: styles.messageInput,
      style: { lineHeight: `${TEXTAREA_LINE_HEIGHT_PX}px` },
      "data-testid": "message-input",
    };

    switch (inputType) {
      case InterviewQuestionInputType.LONG_TEXT:
        return <textarea ref={setInputElementRef} {...commonProps} rows={1} />;

      case InterviewQuestionInputType.NUMERICAL:
        return (
          <NumericFormat
            getInputRef={setInputElementRef}
            value={value}
            onValueChange={(values) => {
              if (values.value === lastHandledValueRef.current) return;
              lastHandledValueRef.current = values.value;
              setValue(values.value);
            }}
            onKeyDown={
              handleKeyDown as unknown as (
                e: React.KeyboardEvent<HTMLInputElement>,
              ) => void
            }
            // Not "Enter digits only" (the phone string): NumericFormat here
            // sets no decimalScale, so 2.5 is valid input and a digits-only
            // prompt would misdescribe the field. Matches 2.0's wording.
            placeholder={translate('Enter numbers only (e.g. "2.5")')}
            disabled={disabled}
            className={styles.messageInput}
            data-testid="message-input"
            thousandSeparator=","
            allowNegative={false}
          />
        );

      case InterviewQuestionInputType.PHONE:
        return (
          <PhoneInput
            initialValue={initialValue}
            onValueChange={(formatted, valid) => {
              setValue(formatted);
              setIsValid(valid);
              setShowValidationError(false);
            }}
            onKeyDown={handleKeyDown}
            inputRef={setInputElementRef}
            disabled={disabled}
            setRegionSelectorRef={setPhoneRegionSelectorRef}
            translate={translate}
          />
        );

      case InterviewQuestionInputType.TEXT:
      default:
        return <input ref={setInputElementRef} type="text" {...commonProps} />;
    }
  }, [
    value,
    handleKeyDown,
    placeholder,
    disabled,
    inputType,
    initialValue,
    translate,
  ]);

  return (
    <>
      <div
        className={styles.messageInputWrapper}
        onClick={(e) => {
          // Clicking the wrapper focuses the input, but exclude the phone
          // region selector which has its own input
          if (e.target !== phoneRegionSelectorRef && inputElementRef) {
            inputElementRef.focus();
          }
        }}
        ref={wrapperRef}
      >
        {inputEl}
        {!disabled && value.trim().length > 0 && (
          <ActionIcon
            aria-label={translate("Send Message")}
            className={styles.sendButton}
            onClick={handleSend}
            variant="transparent"
            data-testid="send-button"
          >
            <SendIcon />
          </ActionIcon>
        )}
      </div>
      {showValidationError && (
        <div className={styles.validationError}>
          {translate(
            "Phone number does not appear valid for the selected region",
          )}
        </div>
      )}
    </>
  );
}
